import { Link, useParams } from "react-router-dom"
import InnerScreenHeader from "@/components/layout/inner-screen-header"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import CopyIcon from "@/components/icons/copy"
import { useCopyToClipboard } from "usehooks-ts"
import { toast } from "sonner"
import { getShortUrl } from "@/api/payment-pages"
import { useInvoice } from "@/api/invoices"
import Loader from "@/components/layout/loader"
import {
  ViewForm,
  ViewFormColumn,
  ViewFormItem,
} from "@/components/ux/view-form"
import InvoiceBadge from "./invoice.badge"
import ArrowDownIcon from "@/components/icons/arrow-down"
import { InvoiceDate } from "./invoice-date"

export default function InvoiceViewScreen() {
  const { id } = useParams()
  const { invoice } = useInvoice(id)
  const [, copy] = useCopyToClipboard()

  if (!invoice) {
    return <Loader message="Loading invoice..." />
  }

  const handleCopy = async () => {
    await copy(getShortUrl(invoice.paymentPage.shortId))
    toast.success("Link copied to clipboard")
  }

  return (
    <div className="flex flex-col gap-4">
      <InnerScreenHeader title="Invoice" to="/invoices" />
      <Card className="shadow-sm">
        <CardContent>
          <div className="text-xl font-semibold flex justify-between gap-2">
            #{invoice.formattedNumber} <InvoiceBadge status={invoice.status} />
          </div>
          <div className="text-muted-foreground">
            for {invoice.total.toLocaleString()} {invoice.token}
          </div>
          <div className="flex flex-col md:flex-row gap-4 pt-3">
            <div className="flex flex-row gap-4">
              {invoice.canEdit && (
                <Button asChild variant="outline">
                  <Link to={`/invoices/${invoice.id}/edit`}>Edit invoice</Link>
                </Button>
              )}
              {invoice.canPay && (
                <Button onClick={handleCopy} variant="outline">
                  <CopyIcon /> Payment link
                </Button>
              )}
              {!invoice.canEdit && (
                <Button asChild disabled={invoice.canEdit} variant="outline">
                  <a href={invoice.pdfLink}>
                    <ArrowDownIcon />
                    Download PDF
                  </a>
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      <h3 className="text-lg font-semibold">Invoice Details</h3>
      <Card className="shadow-sm">
        <CardContent>
          <ViewForm>
            <ViewFormColumn>
              <ViewFormItem label="Billed to">
                <Link to={`/customers/${invoice.customer.id}`}>
                  {invoice.customer.firstName} {invoice.customer.lastName}
                </Link>
              </ViewFormItem>
              <ViewFormItem label="Email">
                {invoice.customer.email}
              </ViewFormItem>
              <ViewFormItem label="Number">
                #{invoice.formattedNumber}
              </ViewFormItem>
            </ViewFormColumn>
            <ViewFormColumn>
              <ViewFormItem label="Total">
                {invoice.total.toLocaleString()} {invoice.token}
              </ViewFormItem>
              <ViewFormItem label="Date">
                <InvoiceDate date={invoice.date} />
              </ViewFormItem>
              <ViewFormItem label="Due Date">
                <InvoiceDate date={invoice.dueDate} />
              </ViewFormItem>
            </ViewFormColumn>
          </ViewForm>
        </CardContent>
      </Card>
    </div>
  )
}
